import { Injectable } from '@kaokei/use-vue-service';
import axios from '@/utils/request';
import { domains } from '@/utils';

import {
  AreaList,
  Captcha,
  CheatRecord,
  CheckRecord,
  ContestInfo,
  ContestDetailInfo,
  ContestDetailInfoList,
  HomeBanner,
  MonitorRecord,
  NavBarList,
  ScheduleList,
  SchoolList,
  Token,
  UploadConfig,
  UserCreation,
  UserCreationList,
  UserExam,
  UserPlantText,
  UserExamList,
  UserInfo,
  UserRegistration,
  Pagination,
  UserCreationActivityType,
  InfoList,
  QuestionBaseInfoList,
  ExamDto,
  UserAnswerRecord,
  PostUserAnswer,
  FinishObjectiveRecord,
  UserInfo4Exam,
  QuestionDto,
  UserAnswerDto,
  ViewPaperDto,
  MarkObjectResp,
  ContestLoginParams,
  UpdateMobileCheckParams,
  PreStartMockExamResp,
  TrtcRoomParams,
  TrtcRoomResponse,
  TrtcInRoomParams,
  TrtcInRoomResponse,
  IUserLoginConf,
} from '@/types';

@Injectable()
export class DaoService {
  /**
   * 获取当前用户信息
   *
   * @return {*}
   * @memberof DaoService
   */
  public getCurrentUserInfo() {
    return axios.get<UserInfo>(
      `${domains.apiDomain}/contest-exam/userLogin/v1/userInfo`
    );
  }

  /**
   * 获取当前用户信息，并且忽略报错
   *
   * @return {*}
   * @memberof DaoService
   */
  public getCurrentUserInfoIgnoreError() {
    return axios.get<UserInfo>(
      `${domains.apiDomain}/contest-exam/userLogin/v1/userInfo`,
      {
        X_DISABLE_ERROR: true,
      }
    );
  }

  /**
   * 领奖登录页面-绑定手机号-获取验证码
   *
   * @param {string} telPhone
   * @return {*}
   * @memberof DaoService
   */
  public sendVerifiedCode(mobile: string, result: string, uuid: string) {
    return axios.get(`${domains.apiDomain}/contest-exam/userLogin/v1/code`, {
      params: { mobile, result, uuid },
    });
  }

  /**
   * 获取图片验证码
   *
   * @return {*}
   * @memberof DaoService
   */
  public getCaptcha() {
    return axios.get<Captcha>(
      `${domains.apiDomain}/contest-exam/userLogin/v1/captcha`
    );
  }

  /**
   * 使用mobile和code登录
   * 返回token
   *
   * @param {string} mobile
   * @param {string} verifyCode
   * @return {*}
   * @memberof DaoService
   */
  public loginWithMobileCode(mobile: string, verifyCode: string) {
    return axios.post<Token>(
      `${domains.apiDomain}/contest-exam/userLogin/v1/login`,
      {
        mobile,
        verifyCode,
      }
    );
  }

  /**
   * 通过账号密码登录
   *
   * @param {string} account
   * @param {string} password
   * @return {*}
   * @memberof DaoService
   */
  public loginWithAccountPassword(account: string, password: string) {
    return axios.post<Token>(
      `${domains.apiDomain}/contest-exam/userLogin/v1/loginByAccount`,
      {
        account,
        password,
      }
    );
  }

  /**
   * 第三方平台通过账号密码登录
   *
   * @param {string} account
   * @param {string} password
   * @param {string} thirdPlatform
   * @return {*}
   * @memberof DaoService
   */
  public otherPlatformLoginWithAccountPassword(
    account: string,
    password: string,
    thirdPlatform: string
  ) {
    return axios.post<Token>(
      `${domains.apiDomain}/contest-exam/userLogin/v1/loginByThirdPlatform`,
      {
        account,
        password,
        thirdPlatform,
      }
    );
  }

  /**
   * 退出登录
   *
   * @return {*}
   * @memberof DaoService
   */
  public logout() {
    return axios.post<any>(
      `${domains.apiDomain}/contest-exam/userLogin/v1/logout`
    );
  }

  /**
   * 获取用户赛事详情数据
   *
   * @return {*}
   * @memberof DaoService
   */
  public getUserExamDetail(id: number) {
    return axios.get<UserExam>(
      `${domains.apiDomain}/contest-exam/userContest/v1/userContest/${id}`
    );
  }

  /**
   * 获取用户明文数据
   *
   * @return {*}
   * @memberof DaoService
   */
  public getUserPlainText(userId: number, contestInfoId: number) {
    return axios.get<UserPlantText>(
      `${domains.apiDomain}/contest-exam/userContest/v1/getUserDecryptInfo`,
      {
        params: {
          userId,
          contestInfoId,
        },
      }
    );
  }

  /**
   * 获取用户赛事列表
   *
   * @return {*}
   * @memberof DaoService
   */
  public getUserExamList(pageNum = 1, pageSize = 20, status = 1) {
    return axios.get<Pagination<UserExamList>>(
      `${domains.apiDomain}/contest-exam/userContest/v1/userContestList`,
      {
        params: {
          pageNum,
          pageSize,
          status,
        },
      }
    );
  }

  /**
   * 报名接口
   *
   * @param {*} data
   * @return {*}
   * @memberof DaoService
   */
  public register(data: UserRegistration) {
    return axios.post<any>(
      `${domains.apiDomain}/contest-exam/userReg/v1/reg`,
      data
    );
  }

  /**
   * 修改报名信息接口
   *
   * @param {*} data
   * @return {*}
   * @memberof DaoService
   */
  public updataRegister(data: UserRegistration) {
    return axios.post<any>(
      `${domains.apiDomain}/contest-exam/userReg/v1/updateReg`,
      data
    );
  }

  /**
   * 回显用户报名信息
   *
   * @return {*}
   * @memberof DaoService
   */
  public getRegisterInfo() {
    return axios.get<UserRegistration>(
      `${domains.apiDomain}/contest-exam/userReg/v1/regInfo`
    );
  }

  /**
   * 获取上传cdn资源的临时密钥
   *
   * @param {string} [type='video']
   * @return {*}
   * @memberof DaoService
   */
  public getTempStsKey(type = 'video') {
    return axios.get<UploadConfig>(
      `${domains.apiDomain}/contest-support/tools/v1/uploadConfig/${type}`
    );
  }

  /**
   * 获取上传cdn资源的临时密钥
   *
   * @param {string} [type='video']
   * @return {*}
   * @memberof DaoService
   */
  public resetTempStsKey(type = 'video') {
    return axios.post<UploadConfig>(
      `${domains.apiDomain}/contest-support/tools/v1/uploadConfig/${type}`
    );
  }

  /**
   * 新增环境检测记录
   *
   * @return {*}
   * @memberof DaoService
   */
  public addCheckRecord(userExamId?: number) {
    return axios.post<number>(
      `${domains.apiDomain}/contest-support/check/v1/checkRecord`,
      {
        userExamId: userExamId || undefined,
      }
    );
  }

  /**
   * 上报前端日志
   *
   * @return {*}
   * @memberof DaoService
   */
  public reportFrontendLog(data: {
    level: 'info' | 'warn' | 'error';
    message: string;
    timestamp?: number;
    userId?: number;
  }) {
    return axios.post(
      `${domains.apiDomain}/contest-support/frontendLog/v1/report`,
      data
    );
  }

  /**
   * 更新检测结果
   *
   * @param {*} data
   * @return {*}
   * @memberof DaoService
   */
  public updateCheckRecord(data: any) {
    return axios.post<CheckRecord>(
      `${domains.apiDomain}/contest-support/check/v1/checkResult`,
      data
    );
  }

  /**
   * 获取单个优秀作品的详情信息
   *
   * @param {number} id
   * @return {*}
   * @memberof DaoService
   */
  public getUserCreationDetail(id: number) {
    return axios.get<UserCreation>(
      `${domains.apiDomain}/contest-support/userCreation/v1/${id}`
    );
  }

  /**
   * 获取优秀作品分页接口
   *
   * @param {number} [pageNum=1]
   * @param {number} [pageSize=12]
   * @return {*}
   * @memberof DaoService
   */
  public getUserCreationList(
    pageNum = 1,
    pageSize = 12,
    activityType?: number
  ) {
    return axios.get<Pagination<UserCreationList>>(
      `${domains.apiDomain}/contest-support/userCreation/v1/list`,
      {
        params: {
          activityType,
          pageNum,
          pageSize,
        },
      }
    );
  }

  /**
   * 获取优秀作品的活动类型列表
   *
   * @return {*}
   * @memberof DaoService
   */
  public getUserCreationActivityType() {
    return axios.get<Array<UserCreationActivityType>>(
      `${domains.apiDomain}/contest-support/userCreation/v1/activityType`
    );
  }

  /**
   * 获取赛事列表页面的活动类型列表
   *
   * @param {boolean} showBrandSign
   * @return {*}
   * @memberof DaoService
   */
  public getActivityType(showBrandSign: boolean) {
    return axios.get<Array<UserCreationActivityType>>(
      `${domains.apiDomain}/contest-support/contest/v1/activityType?showBrandSign=${showBrandSign}`
    );
  }

  /**
   * 获取用户报名的时候需要的地区信息
   *
   * @param {number} scheduleId
   * @param {number} areaType
   * @param {number} [parentId]
   * @return {*}
   * @memberof DaoService
   */
  public getAreaList4Register(
    scheduleId: [number, undefined, null],
    areaType: number,
    parentId?: number
  ) {
    return axios.get<AreaList>(
      `${domains.apiDomain}/contest-support/area/v1/areaList`,
      {
        params: {
          scheduleId,
          areaType,
          parentId,
        },
      }
    );
  }

  /**
   * 获取所有省份和城市数据
   *
   * @return {*}
   * @memberof DaoService
   */
  public getAreaList() {
    return axios.get<AreaList>(
      `${domains.apiDomain}/area-service/area/v1/listCity`
    );
  }

  /**
   * 获取用户报名的时候需要的学校信息
   *
   * @param {number} provinceId
   * @param {number} cityId
   * @param {string} [schoolName]
   * @return {*}
   * @memberof DaoService
   */
  public getSchoolList4Register(
    provinceId: number,
    cityId: number,
    schoolName?: string
  ) {
    return axios.get<SchoolList>(
      `${domains.apiDomain}/contest-support/area/v1/schoolList`,
      {
        params: {
          provinceId,
          cityId,
          schoolName,
        },
      }
    );
  }

  /**
   * 获取某个赛事对应的组别列表
   *
   * @param {number} contestInfoId
   * @return {*}
   * @memberof DaoService
   */
  public getScheduleList(id: number, params: any) {
    return axios.get<ScheduleList>(
      `${domains.apiDomain}/contest-support/schedule/v1/schedule/${id}`,
      {
        params,
      }
    );
  }

  /**
   * 视频监控记录
   *
   * @param {any} data
   * @return {*}
   * @memberof DaoService
   */
  public monitorRecord(data: any) {
    return axios.post<MonitorRecord>(
      `${domains.apiDomain}/contest-support/monitor/v1/monitorRecord`,
      data
    );
  }

  /**
   * 防作弊记录
   * 忽略这个接口的错误信息
   *
   * @param {*} data
   * @return {*}
   * @memberof DaoService
   */
  public cheatRecord(data: any) {
    return axios.post<CheatRecord>(
      `${domains.apiDomain}/contest-support/cheat/v1/cheatRecord`,
      data
    );
  }

  /**
   * 心跳延续
   *
   * @return {*}
   * @memberof DaoService
   */
  public heartBeat() {
    return axios.post(
      `${domains.apiDomain}/contest-support/heartBeat/v1/heartBeat`
    );
  }

  /**
   * 报名页获取赛事信息
   *
   * @param {number} id
   * @return {*}
   * @memberof DaoService
   */
  public getContestInfo(id: number, isEdit = 0) {
    return axios.get<ContestInfo>(
      `${domains.apiDomain}/contest-support/contest/v1/${id}`,
      {
        params: {
          isEdit,
        },
      }
    );
  }

  /**
   *  赛事详情页获取赛事信息
   *
   * @param {number} id
   * @return {*}
   * @memberof DaoService
   */
  public getContestDetailInfo(id: number, params = {}) {
    return axios.get<ContestDetailInfo>(
      `${domains.apiDomain}/contest-support/contest/v1/detail/${id}`,
      {
        params,
      }
    );
  }

  /**
   * 获取分会场赛事列表
   *
   * @param {string} activityType
   * @param {number} provinceId
   * @param {number} cityId
   * @param {number} districtId
   * @param {number} pageNum
   * @param {number} pageSize
   * @return {*}
   * @memberof DaoService
   */
  public getContestList(query: {
    activityType: number;
    activityId?: number;
    contestId?: number;
    provinceId?: number;
    cityId?: number;
    districtId?: number;
    pageNum: number;
    pageSize: number;
    showBrandSign: boolean;
  }) {
    return axios.get<Pagination<ContestDetailInfoList>>(
      `${domains.apiDomain}/contest-support/contest/v1/list`,
      {
        params: query,
      }
    );
  }

  /**
   * 获取首页导航banner
   *
   * @return {*}
   * @memberof DaoService
   */
  public getHomeBanner() {
    return axios.get<Array<HomeBanner>>(
      `${domains.apiDomain}/contest-support/home/v1/banner`
    );
  }

  /**
   * 获取分会场的banner
   *
   * @param {number} type
   * @return {*}
   * @memberof DaoService
   */
  public getHomeBanner4ActivityType(type: number, activityId?: number) {
    return axios.get<HomeBanner>(
      `${domains.apiDomain}/contest-support/home/v1/banner/${type}`,
      {
        params: { activityId },
      }
    );
  }

  /**
   * 获取首页的所有活动类型的赛区信息
   *
   * @return {*}
   * @memberof DaoService
   */
  public getHomeNavBar(params: any) {
    return axios.get<NavBarList>(
      `${domains.apiDomain}/contest-support/home/v1/navBar`,
      {
        params,
      }
    );
  }

  /**
   * 获取赛事资讯分页接口
   *
   * @return {*}
   * @memberof DaoService
   */
  public getContestNews(pageNum: number, pageSize: number) {
    return axios.get<Pagination<InfoList>>(
      `${domains.apiDomain}/contest-support/contestNews/v1/list`,
      {
        params: {
          pageNum,
          pageSize,
        },
      }
    );
  }

  /**
   * 获取考试倒计时剩余时间
   * 忽略这个接口的报错信息
   *
   * @param {number} userExamId
   * @return {*}
   * @memberof DaoService
   */
  public getRemainTime(userExamId: number) {
    return axios.get<{ remainTime: number }>(
      `${domains.apiDomain}/contest-exam/userExam/v1/examDuration`,
      {
        params: {
          userExamId,
        },
      }
    );
  }

  /**
   * 获取活动须知中的题型数据
   *
   * @param {number} userExamId
   * @return {*}
   * @memberof DaoService
   */
  public getExamInfo(userExamId: number) {
    return axios.get<{
      answerRule: string;
      questionInfoList: QuestionBaseInfoList;
      userName?: string | null;
      commitmentLetterTitle?: string | null;
    }>(`${domains.apiDomain}/contest-exam/userExam/v1/examInfo`, {
      params: {
        userExamId,
      },
    });
  }

  /**
   * 开始考试前需要获取answerId和试卷信息
   *
   * @param {number} userExamId
   * @return {*}
   * @memberof DaoService
   */
  public startExam(userExamId: number) {
    return axios.post<ExamDto>(
      `${domains.apiDomain}/contest-exam/userExam/v1/startExam?userExamId=${userExamId}`
    );
  }

  /**
   * 查看试卷答案以及评分记录
   *
   * @param {number} answerPaperId
   * @return {*}
   * @memberof DaoService
   */
  public viewPaper(answerPaperId: number, areaId: number, otherParams: any) {
    return axios.get<ViewPaperDto>(
      `${domains.apiDomain}/contest-exam/view/v1/viewQuestionPaper?answerPaperId=${answerPaperId}`,
      {
        params: {
          reviewAreaId: areaId || undefined,
          ...otherParams,
        },
      }
    );
  }

  /**
   * 查看题目评分记录
   *
   * @param {number} answerPaperId
   * @return {*}
   * @memberof DaoService
   */
  public viewMarkRecord(
    answerPaperId: number,
    questionId: number,
    questionPaperId: number,
    reviewAreaId?: number
  ) {
    return axios.get<MarkObjectResp>(
      `${domains.apiDomain}/contest-exam/view/v1/viewMarkRecord?answerPaperId=${answerPaperId}&questionId=${questionId}&questionPaperId=${questionPaperId}`,
      {
        params: {
          reviewAreaId: reviewAreaId || undefined,
        },
      }
    );
  }

  /**
   * 获取用户答案
   *
   * @param {number} answerPaperId
   * @return {*}
   * @memberof DaoService
   */
  public getUserAnswer(answerPaperId: number) {
    return axios.get<UserAnswerRecord>(
      `${domains.apiDomain}/contest-exam/userExam/v1/userAnswer`,
      {
        params: {
          answerPaperId,
        },
      }
    );
  }

  /**
   * 获取某道题的题目内容和用户答案
   *
   * @param {number} answerPaperId
   * @param {number} questionId
   * @param {number} questionPaperId
   * @return {*}
   * @memberof DaoService
   */
  public getQuestionAndAnswer(
    answerPaperId: number,
    questionPaperId: number,
    questionId: number
  ) {
    return axios.get<{
      questionResp: QuestionDto;
      userAnswerResp: UserAnswerDto;
    }>(`${domains.apiDomain}/contest-exam/userExam/v1/getQuestionAndAnswer`, {
      params: {
        answerPaperId,
        questionId,
        questionPaperId,
      },
    });
  }

  /**
   * 提交用户的客观题答案
   *
   * @param {*} data
   * @return {*}
   * @memberof DaoService
   */
  public postUserAnswer(data: PostUserAnswer) {
    return axios.post<UserAnswerDto>(
      `${domains.apiDomain}/contest-exam/userExam/v1/userAnswer`,
      data
    );
  }

  /**
   * 提交用户的客观题答案
   *
   * @param {*} data
   * @return {*}
   * @memberof DaoService
   */
  public postFinishObjectiveRecord(data: FinishObjectiveRecord) {
    return axios.get<UserAnswerDto>(
      `${domains.apiDomain}/contest-exam/userExam/v1/finishObjectiveRecord`,
      {
        params: data,
      }
    );
  }

  /**
   * 获取考试中的用户信息
   * 大部分信息是报名时提交的信息
   *
   * @param {number} userExamId
   * @return {*}
   * @memberof DaoService
   */
  public getUserInfo4Exam(userExamId: number) {
    return axios.get<UserInfo4Exam>(
      `${domains.apiDomain}/contest-exam/userExam/v1/userInfo`,
      {
        params: {
          userExamId,
        },
      }
    );
  }

  /**
   * 交卷接口
   *
   * @param {number} userExamId
   * @return {*}
   * @memberof DaoService
   */
  public submitExam(userExamId: number) {
    return axios.post(
      `${domains.apiDomain}/contest-exam/userExam/v1/userSubmit?userExamId=${userExamId}`,
      {},
      {
        X_DISABLE_ERROR: true,
        X_DISABLE_UNWRAP: true,
      }
    );
  }

  /**
   * 获取用户的某场考试的奖状
   *
   * @param {number} userExamId
   * @return {*}
   * @memberof DaoService
   */
  public getUserCertificate(userExamId: number) {
    return axios.get<{ userCertificate: string }>(
      `${domains.apiDomain}/contest-exam/userCertificate/v1/getOne`,
      {
        params: {
          userExamId,
        },
      }
    );
  }

  /**
   * 查询是否需要环境检测
   *
   * @return {*}
   * @memberof DaoService
   */
  public queryGoToEnvCheck() {
    return axios.get<{ checkSign: number }>(
      `${domains.apiDomain}/contest-support/check/v1/checkResult`
    );
  }

  /**
   * 模拟赛重考接口
   * @return {*}
   * @memberof DaoService
   */
  public preStartMockExam(userExamId: number) {
    return axios.post<PreStartMockExamResp>(
      `${domains.apiDomain}/contest-exam/userExam/v1/preStartMockExam?userExamId=${userExamId}`,
      {
        userExamId,
      }
    );
  }

  /**
   * 获取组别下对应赛区的列表信息
   * @param params
   * @returns
   */
  public getAreaNodeList(params: any) {
    return axios.get<any[]>(
      `${domains.apiDomain}/contest-support/area/v1/areaNodeList`,
      {
        params,
      }
    );
  }

  /**
   * 找回登录账号
   */
  public getUserAccountPassword(params: any) {
    return axios.post<any[]>(
      `${domains.apiDomain}/contest-exam/userLogin/v1/accountAndPassword`,
      params
    );
  }

  /**
   * 获取活动信息配置
   * @param params
   * @returns
   */
  public getActivityInfo(params: any) {
    return axios.get(
      `${domains.apiDomain}/contest-support/contest/v1/activityInfo`,
      {
        params,
      }
    );
  }

  /**
   * 获取赛事检测状态
   * @param params
   * @returns
   */
  public getCombinationResult(params: any) {
    return axios.get(
      `${domains.apiDomain}/contest-support/check/v1/combinationResult`,
      {
        params,
      }
    );
  }

  /**
   * 新增或更新人脸检测结果
   * @param params
   * @returns
   */
  public updateFaceCheckResult(params: any) {
    return axios.post(
      `${domains.apiDomain}/contest-support/check/v1/faceResult`,
      params
    );
  }

  /**
   * 新增或更新手机设备检测结果
   * @param params
   * @returns
   */
  public updateMobileCheckResult(params: UpdateMobileCheckParams) {
    return axios.post(
      `${domains.apiDomain}/contest-support/check/v1/mobileResult`,
      params
    );
  }

  /**
   * 获取考前检录的考试信息
   * @param params
   * @returns
   */
  public getExamSummary(params: any) {
    return axios.get(
      `${domains.apiDomain}/contest-exam/userExam/v1/examSummary`,
      { params }
    );
  }

  /**
   * 白名单赛事考试登陆
   *
   * @return {*}
   * @memberof DaoService
   */
  public loginByCarNoAndName(params: ContestLoginParams) {
    return axios.post<{ token: string }>(
      `${domains.apiDomain}/contest-exam/userLogin/v1/loginByCarNoAndName`,
      {
        ...params,
      }
    );
  }

  /**
   * 白名单赛事考试登陆
   *
   * @return {*}
   * @memberof DaoService
   */
  public loginByMobileAndCarNo(params: ContestLoginParams) {
    return axios.post<{ token: string }>(
      `${domains.apiDomain}/contest-exam/userLogin/v1/loginByMobileAndCarNo`,
      {
        ...params,
      }
    );
  }

  /**
   * 获取配置录屏规则 及 按钮展示规则
   * @param params
   * @returns
   */
  public getIntroductionBtnRules(params: any) {
    return axios.get(
      `${domains.apiDomain}/contest-exam/video/v1/introductionVideo/buttonRule`,
      { params }
    );
  }
  /**
   * 获取用户讲解视频
   * @param params
   * @returns
   */
  public getIntroductionVideo(params: any) {
    return axios.get(
      `${domains.apiDomain}/contest-exam/video/v1/introductionVideo`,
      { params }
    );
  }

  /**
   * 保存用户讲解视频
   * @param data
   * @returns
   */
  public saveIntroductionVideo(data: any) {
    return axios.post(
      `${domains.apiDomain}/contest-exam/video/v1/introductionVideo`,
      data
    );
  }

  /**
   * 获取trtc房间信息
   * @param params
   * @returns
   */
  public getTrtcRoomInfo(params: TrtcRoomParams) {
    return axios.get<TrtcRoomResponse>(
      `${domains.apiDomain}/contest-support/monitor/v1/enterRoomInfo`,
      {
        params,
      }
    );
  }

  public getUserIsInRoom(params: TrtcInRoomParams) {
    return axios.get<TrtcInRoomResponse>(
      `${domains.apiDomain}/contest-support/monitor/v1/isInRoom`,
      {
        params,
      }
    );
  }

  /**
   * ai检测上报数据
   * @param data
   * @returns
   */
  public postActionRecords(data: SafeAny) {
    return axios.post<TrtcInRoomResponse>(
      `${domains.apiDomain}/contest-support/antiCheat/v1/cheatRecord`,
      data
    );
  }

  /**
   * 获取准考证信息
   * @param params
   * @returns
   */
  public getExamTicketInfo(params: { userExamId: number }) {
    return axios.get(
      `${domains.apiDomain}/contest-exam/userContest/v1/admissionTicketInfo`,
      { params }
    );
  }

  /**
   * 获取登录页配置
   * @param encryptedContestId
   * @returns
   */
  public async getUserLoginConf(encryptedContestId: string) {
    return axios.get<IUserLoginConf>(
      `${domains.apiDomain}/contest-exam/userLogin/gwpub/v1/loginConfig/${encryptedContestId}`
    );
  }

  /**
   * 配置登录页登录方式
   * @param encryptedContestId
   * @param data
   * @returns
   */
  public async postUserLoginByConf(
    encryptedContestId: string,
    data: {
      account: string;
      password: string;
    }
  ) {
    return axios.post(
      `${domains.apiDomain}/contest-exam/userLogin/gwpub/v1/loginByContest/${encryptedContestId}`,
      data
    );
  }
}
